const BASE_URL = process.env.BUN_PUBLIC_API_BASE_URL;

export interface EventTierPlan {
  tier_id: string;
  tier_number: number;
  tier_name: string;
  price_amount: number;
  price_currency: string;
  minimum_boost: number;
  has_player_checkin: boolean;
  has_badge: boolean;
  has_route_builder: boolean;
  is_visible_on_map: boolean;
  is_discoverable: boolean;
  is_advertisable: boolean;
  is_featured: boolean;
  has_analytics: boolean;
  points_multiplier: number;
  primary_color_hex_code: string;
  secondary_color_hex_code: string;
  is_enabled: boolean;
  created_at: string;
}

export interface CreateTierPayload {
  tier_number: number;
  tier_name: string;
  price_amount?: number;
  price_currency: string;
  minimum_boost?: number;
  has_player_checkin?: boolean;
  has_badge?: boolean;
  has_route_builder?: boolean;
  is_visible_on_map?: boolean;
  is_discoverable?: boolean;
  is_advertisable?: boolean;
  is_featured?: boolean;
  has_analytics?: boolean;
  points_multiplier?: number;
  primary_color_hex_code: string;
  secondary_color_hex_code: string;
}

export interface Event {
  event_id: string;
  created_by: string;
  created_by_username: string;
  club_id: string | null;
  club_name: string | null;
  tier_id: string;
  tier_name: string;
  tier_primary_color_hex_code: string;
  tier_secondary_color_hex_code: string;
  title: string;
  description: string | null;
  status: "draft" | "published" | "cancelled" | "completed";
  starts_at: string;
  ends_at: string | null;
  location_lng: number | null;
  location_lat: number | null;
  location_label: string | null;
  banner_obj_url: string | null;
  boost_score: number;
  created_at: string;
  updated_at: string;
}

export interface EventParticipant {
  user_id: string;
  username: string;
  joined_at: string;
  vehicle_id: string | null;
  vehicle_photo_url: string | null;
  vehicle_nickname: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_type: "car" | "motorcycle" | null;
  vehicle_horsepower: number | null;
  vehicle_drive: "FWD" | "RWD" | "AWD" | null;
  vehicle_transmission: "A" | "M" | null;
  vehicle_engine_position: "front" | "center" | "rear" | null;
}

export interface EventBoost {
  boost_id: string;
  user_id: string;
  points_spent: number;
  created_at: string;
}

export interface PaginatedEvents {
  data: Event[];
  page: number;
  page_size: number;
  has_next: boolean;
}

class EventsProvider {
  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const text = await res.text().catch(() => "Unknown error");
      throw new Error(text || `HTTP ${res.status}`);
    }

    const text = await res.text();
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  // Tiers
  listTiers(): Promise<EventTierPlan[]> {
    return this.request<EventTierPlan[]>("/v1/events/tiers");
  }

  createTier(payload: CreateTierPayload): Promise<EventTierPlan> {
    return this.request<EventTierPlan>("/v1/admin/events/tiers", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  updateTier(
    tierId: string,
    payload: CreateTierPayload,
  ): Promise<EventTierPlan> {
    return this.request<EventTierPlan>(`/v1/admin/events/tiers/${tierId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  deleteTier(tierId: string): Promise<string> {
    return this.request<string>(`/v1/admin/events/tiers/${tierId}`, {
      method: "DELETE",
    });
  }

  // Recommendations
  getRecommended(
    params: {
      page?: number;
      lat?: number;
      lng?: number;
      w_timing?: number;
      w_distance?: number;
      w_club?: number;
      w_tier?: number;
      w_participants?: number;
    } = {},
  ): Promise<PaginatedEvents> {
    const p = new URLSearchParams();
    if (params.page !== undefined) p.set("page", String(params.page));
    if (params.lat !== undefined) p.set("lat", String(params.lat));
    if (params.lng !== undefined) p.set("lng", String(params.lng));
    if (params.w_timing !== undefined)
      p.set("w_timing", String(params.w_timing));
    if (params.w_distance !== undefined)
      p.set("w_distance", String(params.w_distance));
    if (params.w_club !== undefined) p.set("w_club", String(params.w_club));
    if (params.w_tier !== undefined) p.set("w_tier", String(params.w_tier));
    if (params.w_participants !== undefined)
      p.set("w_participants", String(params.w_participants));
    return this.request<PaginatedEvents>(`/v1/events/recommended?${p}`);
  }

  // Text search (published events only)
  searchText(q: string, page: number = 0): Promise<PaginatedEvents> {
    const params = new URLSearchParams({ q, page: String(page) });
    return this.request<PaginatedEvents>(`/v1/events/search/text?${params}`);
  }

  // Admin events
  adminList(page: number = 0, userId?: string): Promise<PaginatedEvents> {
    const params = new URLSearchParams({ page: String(page) });
    if (userId) params.set("user_id", userId);
    return this.request<PaginatedEvents>(`/v1/admin/events?${params}`);
  }

  adminDelete(eventId: string): Promise<string> {
    return this.request<string>(
      `/v1/admin/events/${encodeURIComponent(eventId)}`,
      {
        method: "DELETE",
      },
    );
  }

  getById(eventId: string): Promise<Event> {
    return this.request<Event>(`/v1/events/${encodeURIComponent(eventId)}`);
  }

  // Banner
  uploadBanner(eventId: string, content: string): Promise<string> {
    return this.request<string>(
      `/v1/events/${encodeURIComponent(eventId)}/banner`,
      {
        method: "POST",
        body: JSON.stringify({ content }),
      },
    );
  }

  // Participants & boosts
  listParticipants(eventId: string): Promise<EventParticipant[]> {
    return this.request<EventParticipant[]>(
      `/v1/events/${encodeURIComponent(eventId)}/participants`,
    );
  }

  listBoosts(eventId: string): Promise<EventBoost[]> {
    return this.request<EventBoost[]>(
      `/v1/admin/events/${encodeURIComponent(eventId)}/boosts`,
    );
  }
}

export const eventsProvider = new EventsProvider();
